/**
 * ZIP container reading for .xlsx files — End-of-Central-Directory + Central
 * Directory parsing, STORE and DEFLATE entry extraction.
 *
 * Lifted from apps/ledger-web/src/lib/import_xlsx.ts and generalised to return
 * every named entry so a multi-sheet reader can resolve worksheets by name.
 * Decompression uses the native Compression Streams API, which exists in the
 * browser, bun, and Cloudflare Workers.
 */

import { SpreadsheetError } from '../types.js';

function readU16le(bytes: Uint8Array, offset: number): number {
  if (offset < 0 || offset + 2 > bytes.length) {
    throw new SpreadsheetError(
      'MALFORMED_ZIP',
      'Unexpected end of file while reading .xlsx.',
    );
  }
  return bytes[offset] | (bytes[offset + 1] << 8);
}

function readU32le(bytes: Uint8Array, offset: number): number {
  if (offset < 0 || offset + 4 > bytes.length) {
    throw new SpreadsheetError(
      'MALFORMED_ZIP',
      'Unexpected end of file while reading .xlsx.',
    );
  }
  return (
    (bytes[offset] |
      (bytes[offset + 1] << 8) |
      (bytes[offset + 2] << 16) |
      (bytes[offset + 3] << 24)) >>>
    0
  );
}

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_DIR_SIGNATURE = 0x02014b50;
const LOCAL_HEADER_SIGNATURE = 0x04034b50;
const EOCD_MIN_LEN = 22;
const MAX_ZIP_COMMENT_LEN = 65535;

interface ZipCentralEntry {
  name: string;
  /** 0 = STORE (no compression), 8 = DEFLATE. */
  compression: number;
  compressedSize: number;
  localHeaderOffset: number;
}

function findEocd(bytes: Uint8Array): {
  cdOffset: number;
  entryCount: number;
} {
  const searchStart = Math.max(
    0,
    bytes.length - EOCD_MIN_LEN - MAX_ZIP_COMMENT_LEN,
  );
  for (let i = bytes.length - EOCD_MIN_LEN; i >= searchStart; i--) {
    if (readU32le(bytes, i) === EOCD_SIGNATURE) {
      return {
        entryCount: readU16le(bytes, i + 10),
        cdOffset: readU32le(bytes, i + 16),
      };
    }
  }
  throw new SpreadsheetError(
    'NOT_A_ZIP',
    'This does not look like a valid .xlsx file (no ZIP end-of-central-directory record found).',
  );
}

function parseCentralDirectory(
  bytes: Uint8Array,
  cdOffset: number,
  entryCount: number,
): ZipCentralEntry[] {
  const entries: ZipCentralEntry[] = [];
  let offset = cdOffset;
  const decoder = new TextDecoder('utf-8');

  for (let i = 0; i < entryCount; i++) {
    if (readU32le(bytes, offset) !== CENTRAL_DIR_SIGNATURE) {
      throw new SpreadsheetError(
        'MALFORMED_ZIP',
        'Malformed .xlsx file (bad ZIP central directory entry).',
      );
    }
    const compression = readU16le(bytes, offset + 10);
    const compressedSize = readU32le(bytes, offset + 20);
    const nameLen = readU16le(bytes, offset + 28);
    const extraLen = readU16le(bytes, offset + 30);
    const commentLen = readU16le(bytes, offset + 32);
    const localHeaderOffset = readU32le(bytes, offset + 42);
    const name = decoder.decode(
      bytes.subarray(offset + 46, offset + 46 + nameLen),
    );
    entries.push({ name, compression, compressedSize, localHeaderOffset });
    offset += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
}

function extractEntryBytes(
  bytes: Uint8Array,
  entry: ZipCentralEntry,
): Uint8Array {
  const offset = entry.localHeaderOffset;
  if (readU32le(bytes, offset) !== LOCAL_HEADER_SIGNATURE) {
    throw new SpreadsheetError(
      'MALFORMED_ZIP',
      'Malformed .xlsx file (bad ZIP local file header).',
    );
  }
  const nameLen = readU16le(bytes, offset + 26);
  const extraLen = readU16le(bytes, offset + 28);
  const dataStart = offset + 30 + nameLen + extraLen;
  return bytes.subarray(dataStart, dataStart + entry.compressedSize);
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') {
    throw new SpreadsheetError(
      'DECOMPRESSION_UNAVAILABLE',
      'This platform cannot decompress .xlsx files. Please save the file as CSV and upload that instead.',
    );
  }
  // Copy into a fresh, non-shared ArrayBuffer (respecting subarray bounds) so
  // the Blob gets an ArrayBufferView<ArrayBuffer> as its type demands.
  const stream = new Blob([new Uint8Array(data)])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'));
  const buffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(buffer);
}

/** A ZIP's entries, keyed by name, with a lazy text reader. */
export interface XlsxZip {
  names: string[];
  readText(name: string): Promise<string>;
  has(name: string): boolean;
}

/** Parse a .xlsx byte array into a navigable ZIP. */
export function openXlsxZip(bytes: Uint8Array): XlsxZip {
  const { cdOffset, entryCount } = findEocd(bytes);
  const entries = parseCentralDirectory(bytes, cdOffset, entryCount);
  const byName = new Map<string, ZipCentralEntry>();
  for (const e of entries) byName.set(e.name, e);
  const decoder = new TextDecoder('utf-8');

  return {
    names: entries.map((e) => e.name),
    has: (name) => byName.has(name),
    async readText(name: string): Promise<string> {
      const entry = byName.get(name);
      if (!entry) {
        throw new SpreadsheetError(
          'WORKSHEET_NOT_FOUND',
          `Could not find "${name}" inside this .xlsx file.`,
        );
      }
      const raw = extractEntryBytes(bytes, entry);
      let data: Uint8Array;
      if (entry.compression === 0) {
        data = raw;
      } else if (entry.compression === 8) {
        data = await inflateRaw(raw);
      } else {
        throw new SpreadsheetError(
          'UNSUPPORTED_COMPRESSION',
          `Unsupported .xlsx compression method (${entry.compression}). Please save the file as CSV and upload that instead.`,
        );
      }
      return decoder.decode(data);
    },
  };
}
